import { z } from "zod";
import {
  AssessmentInviteContentSchema,
  type AssessmentInviteContent,
  type AssessmentInviteReminders,
  type AssessmentProviderStatus,
} from "./assessment-invites";
import type { CrmIdentity } from "./crm-identity";

// ---------------------------------------------------------------------------
// Sales teams
// ---------------------------------------------------------------------------

export const ListSalesTeamsInput = z.object({
  businessId: z.string().min(1),
});
export type ListSalesTeamsInput = z.infer<typeof ListSalesTeamsInput>;

/**
 * Grocery store teams (GRA-97): the store is a root team (`chain` set),
 * departments are child teams carrying the report's department code
 * ("29" for "29 - Bageri") so uploads map rows to teams.
 */
export const StoreTeamMetaSchema = z.object({
  chain: z.enum(["ica", "coop", "other"]).optional(),
  departmentCode: z.string().min(1).max(20).optional(),
});
export type StoreTeamMeta = z.infer<typeof StoreTeamMetaSchema>;

export const CreateSalesTeamInput = z.object({
  businessId: z.string().min(1),
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional().default(""),
  parentTeamId: z.string().min(1).nullish(),
  leadMemberId: z.string().min(1).nullish(),
  storeMeta: StoreTeamMetaSchema.nullish(),
});
export type CreateSalesTeamInput = z.infer<typeof CreateSalesTeamInput>;

export const UpdateSalesTeamInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  parentTeamId: z.string().min(1).nullish(),
  leadMemberId: z.string().min(1).nullish(),
  storeMeta: StoreTeamMetaSchema.nullish(),
});
export type UpdateSalesTeamInput = z.infer<typeof UpdateSalesTeamInput>;

export const DeleteSalesTeamInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
});
export type DeleteSalesTeamInput = z.infer<typeof DeleteSalesTeamInput>;

// ---------------------------------------------------------------------------
// Sales team members (employees)
// ---------------------------------------------------------------------------

export const ListSalesTeamsMembersInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
});
export type ListSalesTeamsMembersInput = z.infer<
  typeof ListSalesTeamsMembersInput
>;

export const GetSalesTeamsMemberInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
});
export type GetSalesTeamsMemberInput = z.infer<typeof GetSalesTeamsMemberInput>;

export const CreateSalesTeamsMemberInput = z.object({
  businessId: z.string().min(1),
  name: z.string().min(1).max(160),
  email: z.string().email().optional(),
  title: z.string().max(160).optional().default(""),
  salesTeamId: z.string().min(1).nullish(),
  roleId: z.string().min(1).nullish(),
  managerMemberId: z.string().min(1).nullish(),
  businessMemberUid: z.string().min(1).nullish(),
  /**
   * From the employee import (GRA-172); the same field the WhatsApp outbox
   * reads (`mobile`, GRA-291) - normalised to E.164 by the router before
   * this reaches the db layer. Both optional.
   */
  mobile: z.string().trim().max(32).nullish(),
  employeeNumber: z.string().trim().max(32).nullish(),
  /**
   * WhatsApp consent (GRA-291, also used by GRA-292's ad-hoc send gate).
   * Default off - a member must opt in with a number on file.
   */
  whatsappEnabled: z.boolean().optional(),
});
export type CreateSalesTeamsMemberInput = z.infer<
  typeof CreateSalesTeamsMemberInput
>;

export const UpdateSalesTeamsMemberInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  name: z.string().min(1).max(160).optional(),
  email: z.string().email().nullish(),
  title: z.string().max(160).nullish(),
  salesTeamId: z.string().min(1).nullish(),
  roleId: z.string().min(1).nullish(),
  managerMemberId: z.string().min(1).nullish(),
  businessMemberUid: z.string().min(1).nullish(),
  mobile: z.string().trim().max(32).nullish(),
  employeeNumber: z.string().trim().max(32).nullish(),
  /** WhatsApp consent (GRA-291). */
  whatsappEnabled: z.boolean().optional(),
});
export type UpdateSalesTeamsMemberInput = z.infer<
  typeof UpdateSalesTeamsMemberInput
>;

/**
 * Self-service surface (GRA-291): a member edits their own WhatsApp number
 * and consent, or their manager does it for them, from the same settings/
 * profile UI. Deliberately narrower than `UpdateSalesTeamsMemberInput` - the
 * router grants this to the member themselves (not just managers), so it
 * must not be able to touch name/team/role/etc.
 */
export const UpdateMemberWhatsAppSettingsInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  /** E.164 after router-side normalisation; null clears the number. */
  mobile: z.string().trim().max(32).nullish(),
  whatsappEnabled: z.boolean().optional(),
});
export type UpdateMemberWhatsAppSettingsInput = z.infer<
  typeof UpdateMemberWhatsAppSettingsInput
>;

export const DeleteSalesTeamsMemberInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
});
export type DeleteSalesTeamsMemberInput = z.infer<
  typeof DeleteSalesTeamsMemberInput
>;

// ---------------------------------------------------------------------------
// Assessment sessions (testtillfällen) — one doc per testing moment; each
// uploaded PDF is a "result file" keyed by provider + instrument (e.g.
// Capacio cognitive vs personality — two tests, two PDFs, one session).
// Legacy Firestore docs used top-level `cognitive` / `personality` fields only.
// ---------------------------------------------------------------------------

/** Default vendor for sessions created before `providerId` existed. */
export const DEFAULT_ASSESSMENT_PROVIDER_ID = "capacio" as const;

export const AssessmentFileKind = z.enum(["cognitive", "personality"]);
export type AssessmentFileKind = z.infer<typeof AssessmentFileKind>;

// Structured payloads written by the engine PDF parser. Optional everywhere so
// the trigger can populate them asynchronously after the file is uploaded.

const ScoredTraitSchema = z.object({
  score: z.number().nullable(),
  percentile: z.number().nullable(),
  label: z.string().nullable(),
});
export type ScoredTrait = z.infer<typeof ScoredTraitSchema>;

export const CognitiveParsedDataSchema = z.object({
  overall: z
    .object({
      score: z.number().nullable(),
      percentile: z.number().nullable(),
      label: z.string().nullable(),
    })
    .nullable(),
  subscales: z.array(
    z.object({
      name: z.string(),
      score: z.number().nullable(),
      percentile: z.number().nullable(),
    }),
  ),
});
export type CognitiveParsedData = z.infer<typeof CognitiveParsedDataSchema>;

export const PersonalityParsedDataSchema = z.object({
  bigFive: z
    .object({
      openness: ScoredTraitSchema,
      conscientiousness: ScoredTraitSchema,
      extraversion: ScoredTraitSchema,
      agreeableness: ScoredTraitSchema,
      emotionalStability: ScoredTraitSchema,
    })
    .nullable(),
  additionalTraits: z.array(
    z.object({
      name: z.string(),
      percentile: z.number().nullable(),
      description: z.string().nullable(),
    }),
  ),
});
export type PersonalityParsedData = z.infer<typeof PersonalityParsedDataSchema>;

/** Forward-compatible structured payload for unknown provider/instrument pairs. */
export const GenericParsedDataEnvelopeSchema = z.object({
  _graftusParsedEnvelope: z.literal(true),
  providerId: z.string(),
  instrumentKey: z.string(),
  schemaVersion: z.number().int(),
  payload: z.unknown(),
});
export type GenericParsedDataEnvelope = z.infer<
  typeof GenericParsedDataEnvelopeSchema
>;

export const AssessmentParsedDataSchema = z.union([
  CognitiveParsedDataSchema,
  PersonalityParsedDataSchema,
  GenericParsedDataEnvelopeSchema,
]);
export type AssessmentParsedData = z.infer<typeof AssessmentParsedDataSchema>;

const AssessmentFileInput = z.object({
  storagePath: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1).default("application/pdf"),
  sizeBytes: z.number().int().nonnegative().nullish(),
  parsedSummary: z.string().nullish(),
});
export type AssessmentFileInput = z.infer<typeof AssessmentFileInput>;

export const ListAssessmentSessionsInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
});
export type ListAssessmentSessionsInput = z.infer<
  typeof ListAssessmentSessionsInput
>;

/** Lifecycle for provider-invited sessions (Capacio m.fl.). */
export const AssessmentSessionStatusSchema = z.enum([
  "pending",
  /** Created with its provider invitation, email held until `scheduledFor` (GRA-173). */
  "scheduled",
  "completed",
  "expired",
  "cancelled",
]);
export type AssessmentSessionStatus = z.infer<
  typeof AssessmentSessionStatusSchema
>;

export const EnsureProviderCampaignInput = z.object({
  businessId: z.string().min(1),
  providerId: z.string().min(1).max(64),
});
export type EnsureProviderCampaignInput = z.infer<
  typeof EnsureProviderCampaignInput
>;

export const CreateAssessmentInvitesInput = z.object({
  businessId: z.string().min(1),
  providerId: z.string().min(1).max(64),
  memberIds: z.array(z.string().min(1)).max(200).default([]),
  /** Alternative selection: every member of these teams (GRA-173). */
  salesTeamIds: z.array(z.string().min(1)).max(100).optional(),
  /** Optional personal note from the sender, included in the invite email. */
  message: z.string().trim().max(2000).optional(),
  /** ISO instant to send at; absent = send now. 2 minutes to 14 days ahead. */
  scheduledAt: z.string().datetime({ offset: true }).optional(),
  /** Automatic reminders after 3 and 7 days to those not finished (default on). */
  reminders: z.boolean().optional(),
  /** The signature line of the email ("Varma hälsningar, {manager}, {unit}"). */
  unitName: z.string().trim().max(120).optional(),
});
export type CreateAssessmentInvitesInput = z.infer<
  typeof CreateAssessmentInvitesInput
>;

export const ListAssessmentInvitesInput = z.object({
  businessId: z.string().min(1),
  limit: z.number().int().positive().max(500).optional().default(80),
});
export type ListAssessmentInvitesInput = z.infer<
  typeof ListAssessmentInvitesInput
>;

const AssessmentProviderInstrumentLinkSchema = z.object({
  inviteId: z.string().min(1),
  userId: z.string().optional(),
  sessionId: z.string().optional(),
  linkedAt: z.string().optional(),
});

export const AssessmentProviderInstrumentsMetadataSchema = z.object({
  instruments: z.object({
    cognitive: AssessmentProviderInstrumentLinkSchema.optional(),
    personality: AssessmentProviderInstrumentLinkSchema.optional(),
  }),
});

export const CreatePendingAssessmentInviteSessionInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  sessionId: z.string().min(1),
  providerId: z.string().min(1).max(64),
  providerCampaignId: z.string().min(1),
  providerInviteId: z.string().min(1),
  invitedBy: z.string().min(1),
  recipient: z.object({
    memberId: z.string().min(1),
    email: z.string().email().nullable(),
    name: z.string().min(1).max(200),
  }),
  label: z.string().max(160).nullish(),
  providerMetadata: AssessmentProviderInstrumentsMetadataSchema.optional(),
  // ── Scheduled invitations and reminders (GRA-173) ──
  /** "scheduled" holds the email until `scheduledFor`; default "pending" (sent now). */
  status: z.enum(["pending", "scheduled"]).optional(),
  scheduledFor: z.string().nullish(),
  salesTeamId: z.string().nullish(),
  inviteBatchId: z.string().nullish(),
  /** What the send and reminder tasks need without the provider: the URLs and the greeting. */
  invite: AssessmentInviteContentSchema.optional(),
  remindersEnabled: z.boolean().optional(),
  attestationTextVersion: z.string().nullish(),
});
export type CreatePendingAssessmentInviteSessionInput = z.infer<
  typeof CreatePendingAssessmentInviteSessionInput
>;

const SessionResultFileCreateInput = z.object({
  instrumentKey: z.string().min(1).max(64),
  file: AssessmentFileInput,
});

export const CreateAssessmentSessionInput = z
  .object({
    businessId: z.string().min(1),
    memberId: z.string().min(1),
    sessionId: z.string().min(1),
    label: z.string().max(160).nullish(),
    takenAt: z.string().min(1).nullish(),
    providerId: z.string().min(1).max(64).default(DEFAULT_ASSESSMENT_PROVIDER_ID),
    providerMetadata: z.record(z.string(), z.unknown()).optional(),
    /** Map key = stable id for this upload (e.g. UUID); used in Storage path + Firestore map. */
    resultFiles: z.record(z.string().min(1).max(128), SessionResultFileCreateInput),
  })
  .superRefine((val, ctx) => {
    const keys = Object.keys(val.resultFiles);
    if (keys.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one result file is required",
        path: ["resultFiles"],
      });
      return;
    }
    const seen = new Set<string>();
    for (const row of Object.values(val.resultFiles)) {
      if (seen.has(row.instrumentKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate instrumentKey: ${row.instrumentKey}`,
          path: ["resultFiles"],
        });
        return;
      }
      seen.add(row.instrumentKey);
    }
  });
export type CreateAssessmentSessionInput = z.infer<
  typeof CreateAssessmentSessionInput
>;

export const AddSessionFileInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  sessionId: z.string().min(1),
  resultFileId: z.string().min(1).max(128),
  instrumentKey: z.string().min(1).max(64),
  file: AssessmentFileInput,
});
export type AddSessionFileInput = z.infer<typeof AddSessionFileInput>;

export const RemoveSessionFileInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  sessionId: z.string().min(1),
  resultFileId: z.string().min(1).max(128),
});
export type RemoveSessionFileInput = z.infer<typeof RemoveSessionFileInput>;

export const DeleteAssessmentSessionInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  sessionId: z.string().min(1),
});
export type DeleteAssessmentSessionInput = z.infer<
  typeof DeleteAssessmentSessionInput
>;

// ---------------------------------------------------------------------------
// Sales insight threads (persisted AI chat history)
// ---------------------------------------------------------------------------

export const SalesInsightScope = z.enum(["team", "member", "business"]);
export type SalesInsightScope = z.infer<typeof SalesInsightScope>;

export const SalesInsightMessageSchema = z.object({
  id: z.string().min(1),
  role: z.enum(["user", "assistant", "system"]),
  parts: z.array(z.unknown()),
  // AI SDK message-level metadata (e.g. promptVersion, modelId,
  // contextFingerprint emitted by the chat handler). Optional + opaque so
  // future fields don't require a schema bump.
  metadata: z.unknown().nullish(),
  createdAt: z.string().min(1),
});
export type SalesInsightMessage = z.infer<typeof SalesInsightMessageSchema>;

export const ListSalesInsightThreadsInput = z.object({
  businessId: z.string().min(1),
  scope: SalesInsightScope.optional(),
  salesTeamId: z.string().min(1).nullish(),
  memberId: z.string().min(1).nullish(),
  limit: z.number().int().positive().max(100).optional().default(50),
});
export type ListSalesInsightThreadsInput = z.infer<
  typeof ListSalesInsightThreadsInput
>;

export const GetSalesInsightThreadInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1),
});
export type GetSalesInsightThreadInput = z.infer<
  typeof GetSalesInsightThreadInput
>;

export const FindSalesInsightThreadInput = z.object({
  businessId: z.string().min(1),
  topicKey: z.string().min(1),
  scope: SalesInsightScope,
  salesTeamId: z.string().min(1).nullish(),
  memberId: z.string().min(1).nullish(),
});
export type FindSalesInsightThreadInput = z.infer<
  typeof FindSalesInsightThreadInput
>;

export const UpsertSalesInsightThreadInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1).optional(),
  topicKey: z.string().min(1),
  title: z.string().min(1).max(200).optional(),
  scope: SalesInsightScope,
  salesTeamId: z.string().min(1).nullish(),
  memberId: z.string().min(1).nullish(),
  messages: z.array(SalesInsightMessageSchema),
});
export type UpsertSalesInsightThreadInput = z.infer<
  typeof UpsertSalesInsightThreadInput
>;

export const DeleteSalesInsightThreadInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1),
});
export type DeleteSalesInsightThreadInput = z.infer<
  typeof DeleteSalesInsightThreadInput
>;

export const GetSalesInsightThreadAttachmentDownloadInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1),
  attachmentId: z.string().min(1),
});
export type GetSalesInsightThreadAttachmentDownloadInput = z.infer<
  typeof GetSalesInsightThreadAttachmentDownloadInput
>;

export const GetIntelligenceScopeInput = z.object({
  businessId: z.string().min(1),
});
export type GetIntelligenceScopeInput = z.infer<typeof GetIntelligenceScopeInput>;

export interface IntelligenceScopeResult {
  mode: "org" | "scoped";
  linkedSalesMemberId: string | null;
  visibleTeamIds: string[];
  visibleMemberIds: string[];
  canManageStructure: boolean;
  canSubmitPulse: boolean;
  /** "full" = all modules; "team" = team-scoped modules only. Owners/admins always "full". */
  accessLevel: "full" | "team";
}

export const SetTeamManagerInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
  /** Platform user uid to set as team manager, or null to clear. */
  managerUid: z.string().min(1).nullable(),
});
export type SetTeamManagerInput = z.infer<typeof SetTeamManagerInput>;

export const SetStoreRoleInput = z.object({
  businessId: z.string().min(1),
  memberUid: z.string().min(1),
  storeRole: z.enum(["super", "adminchef", "admin", "medarb"]),
});
export type SetStoreRoleInput = z.infer<typeof SetStoreRoleInput>;

export const SetMemberAccessLevelInput = z.object({
  businessId: z.string().min(1),
  memberUid: z.string().min(1),
  accessLevel: z.enum(["full", "team", "none"]),
});
export type SetMemberAccessLevelInput = z.infer<typeof SetMemberAccessLevelInput>;

export const MembershipPermissionsSchema = z.object({
  reportUpload: z.boolean().optional(),
  reportView: z.enum(["none", "store"]).optional(),
});

export const SetMemberPermissionsInput = z.object({
  businessId: z.string().min(1),
  memberUid: z.string().min(1),
  permissions: MembershipPermissionsSchema,
});
export type SetMemberPermissionsInput = z.infer<typeof SetMemberPermissionsInput>;

// ---------------------------------------------------------------------------
// Result interfaces
// ---------------------------------------------------------------------------

export interface SalesTeamRecord {
  id: string;
  businessId: string;
  name: string;
  description: string;
  parentTeamId: string | null;
  leadMemberId: string | null;
  storeMeta?: StoreTeamMeta | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * AI-written summary *of the person* — synthesised nightly from everything we
 * know (assessments, pulse trajectory, KPI values, insights, memory).
 *
 * Distinct from an assessment file's `parsedSummary`, which describes one test
 * report. Concatenating those was what the member view used to show, and it
 * read as a stack of test results rather than a picture of a colleague.
 */
export interface MemberAiSummary {
  text: string;
  generatedAt: string | null;
  promptVersion: number | null;
  modelId: string | null;
  /**
   * Hash of everything that went into producing this text (prompt version +
   * instructions + the assembled member prompt). The nightly run compares it
   * before calling the model: identical inputs mean the model would write the
   * same summary again, so the call is skipped. Null on summaries written
   * before fingerprinting existed — those regenerate once, then stabilise.
   */
  inputFingerprint: string | null;
}

export interface SalesTeamsMemberRecord {
  id: string;
  businessId: string;
  name: string;
  email: string | null;
  title: string;
  salesTeamId: string | null;
  roleId: string | null;
  managerMemberId: string | null;
  businessMemberUid: string | null;
  /** Mobile number and employer's employee number (GRA-172); null when unknown. */
  mobile: string | null;
  employeeNumber: string | null;
  /**
   * WhatsApp consent (GRA-291, also used by GRA-292's ad-hoc send gate):
   * the outbox only sends a real WhatsApp message in production when this
   * is true AND `mobile` is set - otherwise delivery falls back to email.
   * Default false; set via the member's own settings/profile UI, their
   * manager, or "STOPP"/"STOP" turning it off.
   */
  whatsappEnabled: boolean;
  /**
   * Which CRM user this member is, one per toolkit (GRA-56). Empty until an
   * admin maps or imports them; gathers then attribute by the CRM user id.
   */
  crmIdentities: CrmIdentity[];
  latestSessionAt: string | null;
  sessionCount: number;
  /** Null until the nightly run has produced one. */
  aiSummary: MemberAiSummary | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** One uploaded assessment outcome (e.g. a single PDF) plus parse metadata. */
export interface AssessmentFile {
  storagePath: string;
  fileName: string;
  contentType: string;
  sizeBytes: number | null;
  parsedSummary: string | null;
  parsedData: AssessmentParsedData | null;
  parsedAt: string | null;
  parseError: string | null;
  schemaVersion: number | null;
  uploadedAt: string | null;
  uploadedBy: string | null;
}

/** Result file row: same as `AssessmentFile` plus identity for provider routing. */
export interface AssessmentResultFileRecord extends AssessmentFile {
  id: string;
  instrumentKey: string;
}

export interface AssessmentSessionInviteRecipient {
  memberId: string;
  email: string | null;
  name: string;
}

export type AssessmentSessionOwner = "salesTeamMember" | "user";

export interface AssessmentSessionRecord {
  id: string;
  memberId: string;
  businessId: string;
  label: string | null;
  takenAt: string | null;
  /** Assessment vendor (e.g. capacio). Null only for unmigrated legacy docs. */
  providerId: string | null;
  providerMetadata: Record<string, unknown> | null;
  /** One entry per uploaded file; map key matches `id` on each value. */
  resultFiles: Record<string, AssessmentResultFileRecord>;
  createdAt: string | null;
  updatedAt: string | null;
  /** Pending until result files exist or invite cancelled/expired. */
  status: AssessmentSessionStatus;
  isProviderInvite: boolean;
  needsCompletionCheck: boolean;
  invitedAt: string | null;
  invitedBy: string | null;
  expiresAt: string | null;
  recipient: AssessmentSessionInviteRecipient | null;
  providerInviteId: string | null;
  providerUserId: string | null;
  /** Capacio test-session-id from return-uri. */
  providerSessionId: string | null;
  providerCampaignId: string | null;
  inviteError: string | null;
  // ── Scheduled invitations and reminders (GRA-173); null/empty on older sessions ──
  salesTeamId: string | null;
  scheduledFor: string | null;
  /** When the email actually went out (now, or at `scheduledFor`). */
  sentAt: string | null;
  inviteBatchId: string | null;
  invite: AssessmentInviteContent | null;
  reminders: AssessmentInviteReminders;
  providerStatus: AssessmentProviderStatus | null;
  /** Whether the session lives under Users/{uid} or Businesses/.../salesTeamsMembers/{mid}. */
  sessionOwner: AssessmentSessionOwner;
  /** Structured trait scores from provider API (stage1+stage2, normalised). */
  structuredResult: NormalisedAssessmentResult | null;
}

export const NormalisedAssessmentResultSchema = z.object({
  providerId: z.string(),
  campaignId: z.string(),
  providerUserId: z.string(),
  completedAt: z.string(),
  scores: z.record(z.string(), z.number()),
  raw: z.object({ stage1: z.unknown(), stage2: z.unknown() }),
  version: z.number(),
});
export type NormalisedAssessmentResult = z.infer<
  typeof NormalisedAssessmentResultSchema
>;

/** Stored under Businesses/{id}.assessmentProviders.{providerId} */
export interface BusinessAssessmentProviderCampaignRecord {
  campaignId: string;
  campaignName: string;
  createdAt: string | null;
  createdBy: string | null;
}

export interface SalesInsightThreadSummary {
  id: string;
  businessId: string;
  topicKey: string;
  title: string | null;
  scope: SalesInsightScope;
  salesTeamId: string | null;
  memberId: string | null;
  messageCount: number;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** File generated in or linked to a sales insight thread (e.g. action-plan PDF). */
export interface SalesInsightThreadAttachment {
  id: string;
  kind: "action_plan_pdf";
  planTitle: string;
  fileName: string;
  storagePath: string;
  createdAt: string | null;
  createdByUid: string | null;
}

export interface SalesInsightThreadRecord extends SalesInsightThreadSummary {
  messages: SalesInsightMessage[];
  attachments: SalesInsightThreadAttachment[];
}

// ---------------------------------------------------------------------------
// Intelligence topic configuration (per-business overrides on top of presets)
// ---------------------------------------------------------------------------

export const IntelligenceTopicOverrideSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  starter: z.string().min(1).max(500).optional(),
  promptHint: z.string().min(1).max(4000).optional(),
  hidden: z.boolean().optional(),
});
export type IntelligenceTopicOverride = z.infer<
  typeof IntelligenceTopicOverrideSchema
>;

export const IntelligenceTopicConfigSchema = z.object({
  presetId: z.string().min(1).max(64),
  overrides: z.record(z.string(), IntelligenceTopicOverrideSchema).optional(),
  presetPromptOverride: z.string().min(1).max(8000).optional(),
});
export type IntelligenceTopicConfig = z.infer<
  typeof IntelligenceTopicConfigSchema
>;

export const GetIntelligenceTopicsInput = z.object({
  businessId: z.string().min(1),
});
export type GetIntelligenceTopicsInput = z.infer<
  typeof GetIntelligenceTopicsInput
>;

export const UpdateIntelligenceTopicConfigInput = z.object({
  businessId: z.string().min(1),
  config: IntelligenceTopicConfigSchema,
});
export type UpdateIntelligenceTopicConfigInput = z.infer<
  typeof UpdateIntelligenceTopicConfigInput
>;

export interface IntelligenceTopicRecord {
  key: string;
  title: string;
  starter: string;
  promptHint: string;
}
